import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock3 } from "lucide-react";
import { BLOG_POSTS } from "@/config/blogPosts";
import { SITE } from "@/config/site";

export default function BlogList() {
  useEffect(() => {
    document.title = `Blog: reduce los no-shows de tu clínica dental | ${SITE.name}`;
    document
      .querySelector('meta[name="description"]')
      ?.setAttribute(
        "content",
        "Artículos prácticos para propietarios de clínicas dentales: qué es un no-show, cómo reducir ausencias y qué canales de confirmación funcionan mejor."
      );
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-platinum text-navy">
      <header className="sticky top-0 z-50 border-b border-[#E4E9F2] bg-white/85 backdrop-blur-xl">
        <nav className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Link to="/" data-testid="blog-logo" className="flex items-center">
            <img src="/logo-horizontal.png" alt={SITE.name} className="h-6 w-auto" />
          </Link>
          <Link
            to="/"
            data-testid="blog-back-link"
            className="flex items-center gap-2 text-sm font-semibold text-navy-light hover:text-navy"
          >
            <ArrowLeft className="h-4 w-4" />
            Volver a la web
          </Link>
        </nav>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
        <div className="max-w-3xl">
          <p className="text-sm font-semibold uppercase tracking-widest text-navy-light">Blog</p>
          <h1
            data-testid="blog-title"
            className="mt-3 font-heading text-3xl font-extrabold tracking-tight sm:text-4xl lg:text-[2.6rem] lg:leading-[1.15]"
          >
            Recupera horas de sillón: guías sobre no-shows dentales
          </h1>
          <p className="mt-5 text-base leading-relaxed text-steel sm:text-lg">
            Estrategias prácticas para propietarios y gerentes de clínicas dentales que
            quieren menos huecos vacíos y más agenda productiva.
          </p>
        </div>

        <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {BLOG_POSTS.map((post) => (
            <article
              key={post.slug}
              data-testid={`blog-card-${post.slug}`}
              className="group flex flex-col rounded-xl border border-[#E8EDF5] bg-white p-7 shadow-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-md"
            >
              <div className="flex items-center gap-3 text-xs font-medium text-steel">
                <span className="rounded-full bg-platinum px-3 py-1 font-semibold text-navy-light">
                  {post.category}
                </span>
                <span className="flex items-center gap-1">
                  <Clock3 className="h-3.5 w-3.5" />
                  {post.readTime}
                </span>
              </div>
              <h2 className="mt-4 font-heading text-xl font-bold leading-snug text-navy">
                {post.title}
              </h2>
              <p className="mt-3 flex-1 text-sm leading-relaxed text-steel">
                {post.description}
              </p>
              <Link
                to={`/blog/${post.slug}`}
                data-testid={`blog-read-${post.slug}`}
                className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-navy transition-colors group-hover:text-navy-light"
              >
                Leer artículo
                <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
              </Link>
            </article>
          ))}
        </div>
      </main>
    </div>
  );
}
